import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container,
  Paper,
  Fade,
  InputAdornment,
  Alert,
  Slide
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ozenLogo from '../../assets/ozen.png';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = 'https://api.ozenfinance.com/api/v1/public';

// Функция для получения параметров из URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const PhoneAuth = () => {
  const { t } = useLanguage();
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();
  
  // Получаем id из URL-параметров
  const query = useQuery();
  const guarantorId = query.get('id');

  // Создаем массив refs для полей ввода
  const inputRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null)
  ];

  // Обработчик ввода для каждого поля
  const handleCodeChange = (index, value) => {
    const newValue = value.replace(/\D/g, '');
    if (newValue.length <= 1) {
      const newCode = smsCode.split('');
      newCode[index] = newValue;
      setSmsCode(newCode.join(''));
      
      // Если ввели цифру и есть следующее поле - переходим к нему
      if (newValue && index < 3) {
        inputRefs[index + 1].current?.focus();
      }
    }
  };

  // Обработчик клавиш для каждого поля
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!smsCode[index] && index > 0) {
        // Если поле пустое и нажали backspace - переходим к предыдущему полю
        inputRefs[index - 1].current?.focus();
      } else {
        // Если в поле есть цифра - очищаем её
        const newCode = smsCode.split('');
        newCode[index] = '';
        setSmsCode(newCode.join(''));
      }
    }
  };

  // Добавим поддержку WebOTP API для автоматического получения SMS-кода
  useEffect(() => {
    // Функция будет активна только когда мы на шаге ввода SMS
    if (step === 'sms' && 'OTPCredential' in window) {
      const abortController = new AbortController();
      
      // Запускаем запрос на получение SMS
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: abortController.signal
      }).then(otp => {
        // OTP получен успешно
        if (otp && otp.code) {
          // Заполняем поля SMS кода
          setSmsCode(otp.code);
          
          // Заполняем визуально поля ввода
          for (let i = 0; i < Math.min(otp.code.length, 4); i++) {
            if (inputRefs[i].current) {
              // Обновляем значение поля
              inputRefs[i].current.value = otp.code[i];
            }
          }
          
          // Автоматически отправляем форму
          setTimeout(() => {
            handleSmsSubmit({ preventDefault: () => {} });
          }, 500);
        }
      }).catch(error => {
        // Обработка ошибок неважна, так как это дополнительный функционал
        console.log('WebOTP API не сработал:', error);
      });
      
      // Отменяем наблюдение при размонтировании
      return () => {
        abortController.abort();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (error) {
      const exitTimer = setTimeout(() => {
        setIsExiting(true);
      }, 2700);

      const clearTimer = setTimeout(() => {
        setError('');
        setIsExiting(false);
      }, 3000);

      return () => {
        clearTimeout(exitTimer);
        clearTimeout(clearTimer);
      };
    }
  }, [error]);

  // Проверяем наличие id при загрузке компонента
  useEffect(() => {
    if (!guarantorId) {
      setError('noGuarantorId');
    }
  }, [guarantorId]);

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      
      // Добавляем код страны для API, если его нет
      const mobileNumber = cleanPhone;

      const response = await fetch(`${API_URL}/guarantor/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: guarantorId,
          countryCode: "7",
          mobileNumber: mobileNumber
        })
      });

      console.log('Ответ сервера:', {
        status: response.status,
        statusText: response.statusText
      });

      const data = await response.json();
      console.log('Данные ответа:', data);

      if (data?.success) {
        setStep('sms');
      } else {
        switch (data?.code) {
          case '9999':
            setError('phoneNotFound');
            break;
          default:
            if (response.status >= 500) {
              setError('serverError');
            } else {
              setError('generalError');
            }
        }
      }
    } catch (error) {
      console.error('Ошибка запроса:', error);
      setError('networkError');
    } finally {
      setLoading(false);
    }
  };

  const handleSmsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Отправляем запрос подтверждения:', {
        url: `${API_URL}/guarantor/confirmation`,
        id: guarantorId,
        smsCode: smsCode
      });

      const response = await fetch(`${API_URL}/guarantor/confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: guarantorId,
          smsCode: smsCode
        })
      });

      const data = await response.json();

      if (response.ok && data?.success) {
        sessionStorage.setItem('guarantorData', JSON.stringify(data.body));
        sessionStorage.setItem('guarantorId', guarantorId);
        sessionStorage.setItem('phone', phone);
        sessionStorage.setItem('smsCode', smsCode);
        navigate('/form');
      } else {
        switch (data?.code) {
          case '0006':
            setError('wrongCode');
            break;
          case '9999':
            setError('phoneNotFound');
            break;
          default:
            if (response.status >= 500) {
              setError('serverError');
            } else {
              setError('generalError');
            }
        }
      }
    } catch (error) {
      console.error('Ошибка запроса:', error);
      setError('networkError');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    // Ограничиваем длину до 10 цифр (без учета 7 в начале)
    const limitedNumbers = numbers.length > 10 ? numbers.substring(0, 10) : numbers;
    
    if (limitedNumbers.length === 0) return '';
    if (limitedNumbers.length <= 3) return `${limitedNumbers}`;
    if (limitedNumbers.length <= 6) return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)}`;
    if (limitedNumbers.length <= 8) return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6, 8)}`;
    return `${limitedNumbers.slice(0, 3)} ${limitedNumbers.slice(3, 6)} ${limitedNumbers.slice(6, 8)} ${limitedNumbers.slice(8, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #B2C7DA 0%, #BCE3C4 100%)',
        padding: { xs: '10px', sm: '20px' }
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: { xs: '12px', sm: '16px' },
            borderRadius: '16px',
            backgroundColor: 'white',
            position: 'relative',
            mt: { xs: 2, sm: 3 },
            overflow: 'hidden'
          }}
        >
          {/* Логотип и заголовок */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              mb: 4,
              height: '40px',
              pl: { xs: '16px', sm: '24px' }
            }}
          >
            <Box
              component="img"
              src={ozenLogo}
              alt="Ozen Logo"
              sx={{
                width: '32px',
                height: '32px',
                objectFit: 'contain',
                filter: 'grayscale(100%)',
                opacity: 0.9
              }}
            />
            <Typography 
              variant="h5" 
              component="h1"
              sx={{ 
                fontWeight: 500,
                fontSize: '24px',
                letterSpacing: '-0.5px',
                color: '#1a1a1a'
              }}
            >
              {t('phoneTitle')}
            </Typography>
          </Box>

          {/* Форма ввода телефона */}
          <Fade in={step === 'phone'}>
            <Box sx={{ display: step === 'phone' ? 'block' : 'none' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  textAlign: 'center',
                  color: '#666',
                  px: 2,
                  lineHeight: 1
                }}
              >
                {t('enterPhoneDescription')}
              </Typography>
              <form onSubmit={handlePhoneSubmit}>
                <TextField
                  fullWidth
                  label={t('phoneLabel')}
                  value={phone}
                  onChange={handlePhoneChange}
                  inputProps={{
                    inputMode: "numeric",
                    type: "tel",
                    autoComplete: "tel"
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+7</InputAdornment>
                    ),
                  }}
                  placeholder="XXX XXX XX XX"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    }
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading || phone.replace(/\D/g, '').length < 10}
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: '#000000',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#333333'
                    }
                  }}
                >
                  {loading ? t('sending') : t('getCode')}
                </Button>
              </form>
            </Box>
          </Fade>

          {/* Форма ввода СМС */}
          <Fade in={step === 'sms'}>
            <Box sx={{ display: step === 'sms' ? 'block' : 'none' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  textAlign: 'center',
                  color: '#666',
                  px: 2,
                  lineHeight: 1
                }}
              >
                {t('enterSms')}
                <Box component="span" sx={{ display: 'block', mt: 1, color: '#1a1a1a', fontWeight: 500 }}>
                  {phone}
                </Box>
              </Typography>

              <form onSubmit={handleSmsSubmit}>
                <Box 
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    mb: 3
                  }}
                >
                  {[0,1,2,3].map((index) => (
                    <TextField
                      key={index}
                      inputRef={inputRefs[index]}
                      value={smsCode[index] || ''}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      inputProps={{ 
                        maxLength: 1,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        autoComplete: index === 0 ? "one-time-code" : "off",
                        style: { 
                          textAlign: 'center',
                          padding: '8px',
                          width: '48px',
                          height: '48px',
                          fontSize: '20px',
                          fontWeight: '500'
                        }
                      }}
                      sx={{
                        width: '48px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          height: '48px',
                          width: '48px',
                          padding: 0,
                        }
                      }}
                    />
                  ))}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading || smsCode.length !== 4}
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: '#000000',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: '#333333'
                    }
                  }}
                >
                  {loading ? t('checking') : t('confirm')}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setStep('phone')}
                  disabled={loading}
                  sx={{
                    mt: 2,
                    color: '#666'
                  }}
                >
                  {t('changePhone')}
                </Button>
              </form>
            </Box>
          </Fade>

          {error && (
            <Slide direction="down" in={!isExiting} mountOnEnter unmountOnExit>
              <Alert 
                severity="error"
                variant="filled"
                onClose={() => {
                  setIsExiting(true);
                  setTimeout(() => {
                    setError('');
                    setIsExiting(false);
                  }, 300);
                }}
                sx={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  width: '100%',
                  margin: '0 auto',
                  borderRadius: '0',
                  backgroundColor: '#ef5350',
                  '& .MuiAlert-message': {
                    width: '100%',
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    py: 1
                  },
                  '& .MuiAlert-icon': {
                    opacity: 0.9
                  },
                  animation: `${isExiting ? 'slideUp' : 'slideDown'} 0.3s ease`
                }}
              >
                {t(error)}
              </Alert>
            </Slide>
          )}
        </Paper>
      </Container>
      <Box
        sx={{
          '@keyframes slideDown': {
            '0%': {
              transform: 'translateY(-100%)',
              opacity: 0
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1
            }
          },
          '@keyframes slideUp': {
            '0%': {
              transform: 'translateY(0)',
              opacity: 1
            },
            '100%': {
              transform: 'translateY(-100%)',
              opacity: 0
            }
          }
        }}
      />
    </Box>
  );
};

export default PhoneAuth; 